/**
 * src/net/main-mp.ts — DUNE RAID multiplayer entry (page /duneracer/mp.html).
 *
 * Composes the same systems as ../main.ts (frame order there is documented and
 * is copied here), with two differences:
 *   - the AI RivalPack and the capture harness are replaced by server racers
 *     (src/net/remotes.ts) driven by /ws/duneracer,
 *   - a lobby/loading/HUD/results DOM layer implements the site's game
 *     contract (see the repo CLAUDE.md): host settings, loading gate,
 *     spectator bootstrap, fun-stats results, leaderboard.
 *
 * The original single-player game remains untouched at index.html.
 */
import * as THREE from 'three'
import { Engine } from '../core/engine'
import { Atmosphere } from '../atmos/atmosphere'
import { TerrainSystem } from '../terrain/terrain'
import { initPhysics, Vehicle } from '../vehicle/vehicle'
import { InputSource } from '../vehicle/input'
import { buildBuggy } from '../vehicle/visual'
import { WHEEL_WIDTH } from '../vehicle/config'
import type { VehicleInput } from '../vehicle/types'
import { AssetLibrary } from '../assets/library'
import { RouteSystem, CourseFurniture } from '../route'
import { pointAt } from '../route/generate'
import { FxSystem } from '../fx'
import type { FxVehicleState, FxWheelContact } from '../fx/types'
import { WorldScatter, SCATTER_ASSETS } from '../world/scatter'
import { Landmarks, LANDMARK_ASSETS } from '../world/landmarks'
import { Bivouac, BIVOUAC_ASSETS } from '../world/bivouac'
import { PropPhysics } from '../world/prop-physics'
import { ContactShadows } from '../world/contact-shadows'
import { installAudio } from '../audio'
import { installCameras } from '../camera/install'
import {
  DuneNet, type StateMsg, type FunStatsResults, type RosterEntry,
  type Cosmetics,
} from './net'
import { RemoteRacers } from './remotes'

// ── DOM ───────────────────────────────────────────────────────────────────
const $ = (id: string) => document.getElementById(id) as HTMLElement
const boot = $('boot')
const say = (m: string) => { ($('boot-msg') as HTMLElement).textContent = m }
const showOverlay = (id: string, on: boolean) => {
  $(id).classList.toggle('hidden', !on)
}

// ── shared music/SFX baseline (site contract) ─────────────────────────────
const MUSIC_TRACKS = [
  '/music/alexgrohl-burn-it-down-this-angry-metal-449649.mp3',
  '/music/alexgrohl-electronic-stylish-rock-15045.mp3',
  '/music/hitslab-game-gaming-music-295075.mp3',
  '/music/lightyeartraxx-kim-lightyear-angel-eyes-chiptune-edit-110226.mp3',
]
let musicAudio: HTMLAudioElement | null = null
// Engine-audio handle (installAudio); its setMasterVolume drives the mixer's
// master bus — that graph IS the game SFX (engine/surface/impacts/wind).
let sfxAudio: ReturnType<typeof installAudio> | null = null
// Assigned by setupAudioControls; re-applies the persisted SFX slider/mute
// state to the engine mixer (called again whenever a world installs audio).
let applySfxVolume: () => void = () => {}
let trackIdx = Math.floor(Math.random() * MUSIC_TRACKS.length)
function startMusic() {
  if (!musicAudio) return
  musicAudio.src = MUSIC_TRACKS[trackIdx]!
  musicAudio.play().catch(() => {})
}
function setupAudioControls() {
  musicAudio = new Audio()
  musicAudio.volume = 0.3
  musicAudio.loop = true
  const sfxSlider = $('sfxVolume') as HTMLInputElement
  const musSlider = $('musicVolume') as HTMLInputElement
  const muteSfx = $('muteSfx')
  const muteMusic = $('muteMusic')
  // Reset from style.css's fixed top-right pin.
  const bar = $('audioControls')
  bar.style.position = 'fixed'
  bar.style.top = 'auto'
  let sfxMuted = false
  let musMuted = false
  try {
    sfxMuted = localStorage.getItem('duneSfxMuted') === '1'
    musMuted = localStorage.getItem('duneMusicMuted') === '1'
    musSlider.value = localStorage.getItem('duneMusicVol') ?? '30'
    sfxSlider.value = localStorage.getItem('duneSfxVol') ?? '100'
    musicAudio!.volume = musMuted ? 0 : parseInt(musSlider.value) / 100
  } catch {}
  const applySfx = () => {
    const v = sfxMuted ? 0 : parseInt(sfxSlider.value) / 100
    sfxAudio?.setMasterVolume(v)
  }
  applySfxVolume = applySfx
  applySfx()
  muteSfx.addEventListener('click', () => {
    sfxMuted = !sfxMuted
    try { localStorage.setItem('duneSfxMuted', sfxMuted ? '1' : '0') } catch {}
    muteSfx.textContent = sfxMuted ? '🔇 SFX' : '🔊 SFX'
    applySfxVolume()
  })
  sfxSlider.addEventListener('input', () => {
    applySfxVolume()
    try { localStorage.setItem('duneSfxVol', sfxSlider.value) } catch {}
  })
  muteMusic.addEventListener('click', () => {
    musMuted = !musMuted
    try { localStorage.setItem('duneMusicMuted', musMuted ? '1' : '0') } catch {}
    muteMusic.textContent = musMuted ? '🎵 Music' : '🎶 Music'
    if (musicAudio) musicAudio.volume = musMuted ? 0 : parseInt(musSlider.value) / 100
  })
  musSlider.addEventListener('input', () => {
    if (musicAudio) musicAudio.volume = musMuted ? 0 : parseInt(musSlider.value) / 100
    try { localStorage.setItem('duneMusicVol', musSlider.value) } catch {}
  })
  $('skipTrack').addEventListener('click', () => {
    trackIdx = (trackIdx + 1) % MUSIC_TRACKS.length
    startMusic()
  })
  muteSfx.textContent = sfxMuted ? '🔇 SFX' : '🔊 SFX'
  muteMusic.textContent = musMuted ? '🎵 Music' : '🎶 Music'
}
setupAudioControls()

// ── net state ─────────────────────────────────────────────────────────────
interface WorldCtx {
  engine: Engine
  terrain: TerrainSystem
  route: RouteSystem
  vehicle: Vehicle | null
  input: InputSource | null
  remotes: RemoteRacers
  fx: FxSystem
  cameras: ReturnType<typeof installCameras>
  heroGroup: THREE.Group | null
  fxList: FxVehicleState[]
  uiScene: THREE.Scene
  seed: number
}
let world: WorldCtx | null = null
const net = new DuneNet()
let myId = ''
let isHost = false
let spectator = false
let racing = false
let myName = 'Dune Racer'
let rosterCache: RosterEntry[] = []
let raceOver = false
let topSpeed = 0
let airTime = 0
let odo = 0
let lastPoseSent = 0

net.onClose = () => {
  racing = false
  showOverlay('errorOverlay', true)
}

// ── lobby wiring ──────────────────────────────────────────────────────────
async function initLobby() {
  // Account integration: logged-in users never see the name prompt.
  try {
    const acct = (window as any).Account
    if (acct?.ready) await acct.ready
    if (acct?.user) myName = acct.user.username
  } catch {}
  const stored = (() => {
    try { return localStorage.getItem('mpPlayerName') } catch { return null }
  })() || ''
  const nameInput = $('nameInput') as HTMLInputElement
  if (myName !== 'Dune Racer') {
    nameInput.value = myName
    nameInput.disabled = true                       // "Playing as X"
  } else if (stored) {
    nameInput.value = stored
  }
  ;($('accountChip')).textContent =
    myName !== 'Dune Racer' ? `Playing as ${myName}` : ''

  $('joinBtn').addEventListener('click', joinClicked)
  nameInput.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') joinClicked()
  })
  showOverlay('lobbyPanel', true)
  $('startBtn').addEventListener('click', () => net.send({ type: 'start' }))

  // Compile the terrain shaders NOW, while the player is picking a name and
  // reading the roster, instead of after "Start Race" where it froze the tab
  // for over two minutes on a first visit. Deliberately not awaited: the lobby
  // must stay usable, and a race that starts mid-warm-up simply finds some of
  // the programs already built.
  void warmStageOnce((msg) => {
    const el = $('warmMsg')
    if (msg === 'ready') el.classList.add('hidden')
    else { el.textContent = msg; el.classList.remove('hidden') }
  })
  for (const id of ['setBots', 'setTime']) {
    ($(id) as HTMLSelectElement).addEventListener('change', sendSettings)
  }
}

function sendSettings() {
  if (!isHost) return
  net.send({
    type: 'settings',
    bots: parseInt(($('setBots') as HTMLSelectElement).value),
    timeOfDay: ($('setTime') as HTMLSelectElement).value,
  })
}

async function joinClicked() {
  const nameInput = $('nameInput') as HTMLInputElement
  const name = (nameInput.value || 'Dune Racer').slice(0, 16)
  try { localStorage.setItem('mpPlayerName', name) } catch {}
  myName = name
  showOverlay('joinError', false)
  try {
    await net.connect()
  } catch {
    showOverlay('joinError', true)
    return
  }
  net.send({ type: 'join', name })
  registerHandlers()
  startMusic()
}

function renderRoster(players: RosterEntry[], hostId: string | null) {
  rosterCache = players
  // Remember the roster's cosmetics: the HUD board, the finish toast and the
  // rankings modal look a player up instead of the server repeating them.
  {
    const acct = (window as any).Account
    if (acct?.trackRoster) acct.trackRoster(players)
  }
  const ul = $('playerList')
  ul.innerHTML = ''
  for (const p of players) {
    const li = document.createElement('li')
    if (p.host) li.appendChild(document.createTextNode('👑 '))
    // The name lives in its own span so store cosmetics (badge, name colour,
    // title) decorate the NAME rather than the host crown / spectating tag.
    const nameEl = document.createElement('span')
    nameEl.textContent = p.name
    const acct = (window as any).Account
    if (acct) acct.decorateName(nameEl, p.cosmetics, true)
    li.appendChild(nameEl)
    if (p.spectator) li.appendChild(document.createTextNode(' (spectating)'))
    ul.appendChild(li)
  }
  isHost = !!hostId && hostId === myId
  ;($('startBtn')).classList.toggle('hidden', !isHost)
  ;($('waitText')).classList.toggle('hidden', isHost)
  for (const id of ['setBots', 'setTime']) {
    ($(id) as HTMLSelectElement).disabled = !isHost
  }
}

function registerHandlers() {
  net.on('joined', (m) => {
    myId = m.id
    isHost = m.is_host
    spectator = m.spectator
    showOverlay('lobbyPanel', true)
    ;($('joinCard')).classList.add('hidden')
    ;($('lobbyBody')).classList.remove('hidden')
    renderRoster(m.players,
      m.players.find((p: RosterEntry) => p.host)?.id ?? null)
  })
  net.on('lobby_update', (m) => {
    renderRoster(m.players, m.host)
    if (isHost) {
      ;($('setBots') as HTMLSelectElement).value = String(m.settings.bots)
      ;($('setTime') as HTMLSelectElement).value = m.settings.timeOfDay
    }
    // The room went back to the lobby (host restarted, or the results hold
    // expired). Anyone who has left the lobby screen — finished the race, or
    // is watching one as a late-joining spectator — reloads into the fresh
    // lobby, where the server hands them a car for the next race. Without the
    // spectator/world arms a late joiner stayed stuck on the spectator camera
    // for every subsequent round.
    if (m.game_state === 'lobby' && (raceOver || spectator || world)) {
      location.reload()
    }
  })
  net.on('game_starting', (m) => {
    showOverlay('lobbyPanel', false)
    boot.classList.remove('gone')
    // Arm the next celebration on the transition INTO a race, not on the button
    // that starts one — see WinFx.reset.
    const wfx = (window as any).WinFx
    if (wfx) wfx.reset()
    // `?seed=` pins the world for harnesses, the same way `?wp=/minleg=` pin the
    // stage shape. Every client in a match MUST pass the same value or they
    // generate different worlds; production links carry none and use the
    // server's.
    buildWorld(_qNum('seed', m.seed), m.stage.timeOfDay).catch((e) => {
      console.error(e)
      say(`world failed: ${e}`)
    })
  })
  net.on('loading', (m) => {
    say(m.all_loaded ? 'all loaded' : 'waiting for drivers…')
  })
  net.on('countdown', (m) => {
    const el = $('countdown')
    el.classList.remove('hidden')
    el.textContent = m.count > 0 ? String(m.count) : 'GO!'
    if (m.count === 0) {
      racing = true
      setTimeout(() => el.classList.add('hidden'), 900)
      world?.remotes.snap()
    }
  })
  net.on('state', onState)
  net.on('event', (m) => {
    const ev = m.event
    if (ev.kind === 'waypoint' && ev.player_id === myId) {
      toast(`✓ ${ev.code}`)
    } else if (ev.kind === 'finished' && ev.player_id === myId) {
      finishSelf(ev.place ?? 0)
    } else if (ev.kind === 'finished') {
      const r = rosterCache.find((x) => x.id === ev.player_id)
      toast(r ? `${nameHtml(r.name, r.id, r.cosmetics)} finished P${ev.place}`
              : `A racer finished P${ev.place}`, !!r)
    }
  })
  net.on('match_end', (m) => showResults(m.results, m.winner))
}

let toastTimer = 0
/** `html` only for a line built with `nameHtml` — everything else is text. */
function toast(text: string, html = false) {
  const el = $('toast')
  if (html) el.innerHTML = text
  else el.textContent = text
  el.classList.remove('hidden')
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => el.classList.add('hidden'), 2600)
}

function finishSelf(place: number) {
  racing = false
  raceOver = true
  toast(place > 0 ? `Finished P${place}!` : 'Finished!')
  net.send({ type: 'race_stats', topSpeed, airTime, odo })
}

function showResults(results: FunStatsResults, winner: string | null = null) {
  racing = false
  raceOver = true
  showOverlay('resultsScreen', true)
  // The winner's bought celebration — the shared one, played by every game on
  // the site since 2026-09-09 (web/shared/win_fx.js).
  const wfx = (window as any).WinFx
  if (winner && wfx) {
    const row = results.rows.find((r) => r.player_id === winner)
    wfx.celebrate(winner, row?.cosmetics)
  }
  const tableWrap = $('resultsTableWrap')
  const awardsWrap = $('resultsAwards')
  tableWrap.innerHTML = ''
  awardsWrap.innerHTML = ''
  const fs = (window as any).FunStats
  if (fs?.render) fs.render(results, tableWrap, awardsWrap)
  else {
    // fun_stats.js missing (dev without the site shell): minimal fallback.
    const pre = document.createElement('pre')
    pre.textContent = JSON.stringify(results.rows, null, 2)
    tableWrap.appendChild(pre)
  }
  const capNote = $('capNote')
  capNote.classList.toggle('hidden', !results.round_capped)
  ;($('playAgainBtn')).classList.toggle('hidden', !isHost)
  ;($('waitAgain')).classList.toggle('hidden', isHost)
}

$('playAgainBtn').addEventListener('click', () => {
  net.send({ type: 'restart' })
})
/**
 * Open the cross-session leaderboard. Bound to BOTH entry points — the button
 * on the results overlay and the one in the lobby — so a player who is waiting
 * for a race, or who never finishes one, can still reach it.
 */
async function openRankings() {
  showOverlay('rankingsModal', true)
  const tbody = $('rankingsBody')
  tbody.innerHTML = '<tr><td colspan="5">loading…</td></tr>'
  try {
    const resp = await fetch('/api/duneracer/rankings?limit=200')
    const data = await resp.json()
    const agg = new Map<string, { wins: number; races: number; best: number;
                                  cos: Cosmetics | null }>()
    for (const rec of data) {
      for (const p of rec.players ?? []) {
        if (p.is_bot) continue
        const e = agg.get(p.name)
          ?? { wins: 0, races: 0, best: 99, cos: null }
        e.races++
        if (p.place === 1) e.wins++
        e.best = Math.min(e.best, p.place ?? 99)
        // the row carries the player's cosmetics (annotated server-side)
        e.cos = p as Cosmetics
        agg.set(p.name, e)
      }
    }
    const rows = [...agg.entries()].sort((a, b) =>
      b[1].wins - a[1].wins || a[1].best - b[1].best || b[1].races - a[1].races)
    tbody.innerHTML = rows.slice(0, 25).map(([name, e], i) =>
      `<tr><td>${i + 1}</td><td>${nameHtml(name, name, e.cos)}</td><td>${e.wins}</td>` +
      `<td>${e.races}</td><td>P${e.best === 99 ? '-' : e.best}</td></tr>`).join('')
    if (!rows.length) tbody.innerHTML =
      '<tr><td colspan="5">No racers ranked yet</td></tr>'
  } catch {
    tbody.innerHTML = '<tr><td colspan="5">leaderboard unavailable</td></tr>'
  }
}
$('rankingsBtn').addEventListener('click', openRankings)
$('lobbyRankBtn').addEventListener('click', openRankings)
$('rankingsCloseBtn').addEventListener('click', () =>
  showOverlay('rankingsModal', false))
$('retryBtn').addEventListener('click', () => location.reload())

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!))
}

/**
 * A player name as decorated HTML: badge + name in a span carrying the
 * equipped colour and fx. `key` is a player id or a name (the roster registry
 * answers both); `cos` is an override for rows that carry their own.
 */
function nameHtml(name: string, key?: string, cos?: Cosmetics | null): string {
  const acct = (window as any).Account
  const c: Cosmetics | null = acct?.resolveCosmetics
    ? acct.resolveCosmetics(cos, key ?? name) : null
  if (!c) return escapeHtml(name)
  return `<span${acct.nameStyleAttr(c)}>` +
    `${c.badge ? c.badge + ' ' : ''}${escapeHtml(name)}</span>`
}

// ── HUD ───────────────────────────────────────────────────────────────────
function updateHud(m: StateMsg) {
  ;($('hudClock')).textContent = fmtClock(m.clock)
  const mine = m.racers.find((r) => r.id === myId)
  const place = mine?.place ?? (m.racers.findIndex((r) => r.id === myId) + 1)
  ;($('hudPlace')).textContent = place > 0 ? ordinal(place) : '-'
  if (mine) {
    const frac = Math.max(0, Math.min(1, mine.s / Math.max(1, m.route_len)))
    ;($('hudProgFill')).style.width = `${(frac * 100).toFixed(1)}%`
  }
  const board = m.racers.slice(0, 8).map((r, i) => {
    const nm = r.id === myId ? myName
      : rosterCache.find((c) => c.id === r.id)?.name ??
        (r.bot ? `Rival ${i}` : 'racer')
    return `<div class="hud-row${r.id === myId ? ' me' : ''}">` +
      `<span>${i + 1}. ${r.bot ? escapeHtml(nm) : nameHtml(nm, r.id)}</span>` +
      `<span>${r.finished ? fmtClock(r.place ? m.clock : m.clock) : `${Math.round(r.s)} m`}</span></div>`
  }).join('')
  ;($('hudBoard')).innerHTML = board
}

function fmtClock(t: number): string {
  const mm = Math.floor(t / 60)
  const ss = Math.floor(t % 60)
  return `${mm}:${String(ss).padStart(2, '0')}`
}
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!)
}

// ── world construction (mirrors main.ts frame order) ─────────────────────
// Stage shape is URL-tunable but every client in a match MUST use the same
// values — all clients generate the identical world from the shared seed.
// Production links omit the params and get the standard 6-waypoint stage.
const _qs = new URLSearchParams(location.search)
const _qNum = (k: string, d: number) => (_qs.has(k) ? Number(_qs.get(k)) : d)
const STAGE_ROUTE = {
  waypoints: _qNum('wp', 6),
  minLeg: _qNum('minleg', 300),
  maxLeg: _qNum('maxleg', 450),
  checkpointEvery: 2,
}

/** Camera subject stand-in while spectating (rig is 'free', never reads it). */
const SPECTATOR_SUBJECT = {
  state: {
    position: new THREE.Vector3(), rotation: new THREE.Quaternion(),
    velocity: new THREE.Vector3(), speed: 0, airborne: false,
    chassisRoll: 0, chassisPitch: 0,
    // camera simStep sums wheel load/slip for impact FX — an empty set is a
    // car that never lands, which is what a spectator's camera wants.
    wheels: [] as { load: number; slipSpeed: number }[],
  },
  // collectBeats() iterates `.beats` on every camera source.
  beats: [] as unknown[],
}

/**
 * The one Engine, created on demand and shared.
 *
 * The lobby's shader warm-up has to compile into the same GL context the race
 * will draw from, so the Engine can no longer be born inside buildWorld.
 */
let sharedEngine: Engine | null = null
function getEngine(): Engine {
  if (!sharedEngine) {
    sharedEngine = new Engine($('view') as HTMLCanvasElement)
    sharedEngine.camera.near = 0.4
    sharedEngine.camera.far = 26000
    sharedEngine.camera.updateProjectionMatrix()
  }
  return sharedEngine
}

/**
 * Atmosphere + terrain, built once and reused across races.
 *
 * These two exist before the seed does, on purpose. They own the expensive
 * shader programs — `clipmap.vert` inlines the whole dune field — and a
 * program's identity in three.js depends on the SCENE it is compiled for: its
 * lights, its fog, whether it has an environment map. Warming those programs in
 * a scratch scene compiled something subtly different and the race recompiled
 * from scratch, which is measurable and was measured. So the lobby builds the
 * REAL stage and the race reseeds it.
 *
 * Nothing here is seed-dependent: `uSeed` is a uniform, and `rebuild(seed)` is
 * what turns a stage into a particular world.
 */
let stage: { atmos: Atmosphere; terrain: TerrainSystem } | null = null
function getStage(engine: Engine): { atmos: Atmosphere; terrain: TerrainSystem } {
  if (!stage) {
    const atmos = new Atmosphere(engine.renderer, engine.scene,
      { turbidity: 3.4, groundAlbedo: 0.35 })
    // The lobby's default. Changing it later swaps the environment TEXTURE, not
    // the defines, so the compiled programs stay valid.
    atmos.setTimeOfDay(7.4)
    atmos.updateEnvironment(true)
    const terrain = new TerrainSystem(engine.renderer, 1)
    terrain.attach(engine.scene, engine.camera)
    stage = { atmos, terrain }
  }
  return stage
}

/**
 * The in-flight lobby warm-up, so the world build can wait for it.
 *
 * Without this a player who hits Start Race the moment they join races their
 * own warm-up: the build reaches a program the driver has not finished and the
 * synchronous first-use blocks the main thread anyway — measured at 15+ seconds
 * of dead tab even with the warm-up running. Waiting costs that player nothing
 * (the compile has to finish either way) and buys a loading screen that is
 * still painting.
 */
let warming: Promise<void> | null = null

export function warmStageOnce(onProgress?: (msg: string) => void): Promise<void> {
  warming ??= warmStage(onProgress)
  return warming
}

async function warmStage(onProgress?: (msg: string) => void): Promise<void> {
  const engine = getEngine()
  onProgress?.('preparing terrain…')
  getStage(engine)                      // the bake programs compile in here
  onProgress?.('preparing terrain shaders…')
  // A program's key includes the renderer's tone mapping, and the race draws
  // the scene with it OFF — fx/composer.ts turns it off because dust blends in
  // linear radiance and the tone curve is applied once, in the composite. So
  // warm in that state, or we compile a TONE_MAPPING variant the race never
  // asks for and pay for the real one twice. Restored straight after: only the
  // compile cares.
  const prevTone = engine.renderer.toneMapping
  engine.renderer.toneMapping = THREE.NoToneMapping
  // Same reason, second state: three.js compiles the OUTPUT TRANSFER into the
  // fragment shader from whatever render target is bound. The race draws the
  // scene into the composer's linear HalfFloat target, so warming against the
  // default framebuffer produced an sRGB-encoding variant instead. Any
  // off-screen target gives the linear one; 4x4 is enough, nothing is drawn.
  const prevRT = engine.renderer.getRenderTarget()
  const warmRT = new THREE.WebGLRenderTarget(4, 4, { type: THREE.HalfFloatType })
  engine.renderer.setRenderTarget(warmRT)
  // ANGLE gives us two ways to get these programs built, and they are good at
  // opposite things. Both were measured on the real clipmap program:
  //
  //   synchronous first use  — 45 s cold, but ~0 s once Chrome has the linked
  //                            program in its on-disk cache. Blocks the tab.
  //   KHR_parallel_shader_compile — never blocks, but takes 60 s cold AND 69 s
  //                            warm: the background path does not benefit from
  //                            the disk cache at all.
  //
  // So: the first time this browser ever sees these shaders, take the slow
  // non-blocking road and keep the lobby alive. Every visit after that, the
  // cache is there and the synchronous road is instant.
  const KEY = 'duneracer.shadersWarmed'
  let warmedBefore = false
  try { warmedBefore = localStorage.getItem(KEY) === '1' } catch {}
  const t0 = performance.now()
  try {
    if (!warmedBefore) {
      // Resolves when the driver reports COMPLETION_STATUS_KHR. The page keeps
      // painting the whole time.
      await engine.renderer.compileAsync(engine.scene, engine.camera)
    }
    // The first USE is where three.js reads uniform locations and the driver
    // must have finished. After compileAsync it is free; on a warm cache it is
    // a disk hit. Into a 4x4 target, so the draw itself costs nothing.
    engine.renderer.render(engine.scene, engine.camera)
  } finally {
    engine.renderer.setRenderTarget(prevRT)
    engine.renderer.toneMapping = prevTone
    warmRT.dispose()
  }
  // Coming off the parallel road, Chrome now HAS the linked programs, so the
  // next visit can take the fast synchronous one. Coming off the synchronous
  // road, a slow run means the cache was not there after all (evicted, driver
  // update, different GPU) — drop the claim so the next visit goes back to the
  // road that does not freeze.
  const tookMs = performance.now() - t0
  try {
    localStorage.setItem(KEY, !warmedBefore || tookMs < 5000 ? '1' : '0')
  } catch {}
  // Readiness marker for the E2E harnesses.
  ;(window as unknown as { __duneWarm?: boolean }).__duneWarm = true
  onProgress?.('ready')
}

async function buildWorld(seed: number, timeOfDay: string): Promise<void> {
  const engine = getEngine()

  // On a first visit the terrain shaders may still be compiling. Wait here,
  // where the boot screen is up and animating, rather than hitting a
  // half-compiled program mid-build and freezing the tab.
  say('preparing terrain shaders…')
  await warmStageOnce()

  say('atmosphere…')
  const { atmos, terrain } = getStage(engine)

  say('terrain…')
  // The stage may have been standing since the lobby with a placeholder seed —
  // or since the previous race, with the previous world's.
  terrain.rebuild(seed)

  function syncTerrainLighting() {
    const sunCol = atmos.sun.color.clone()
      .multiplyScalar(atmos.sun.visible ? atmos.sun.intensity : 0)
    const skyCol = atmos.ambient.color.clone()
      .multiplyScalar(atmos.ambient.intensity)
    const groundCol = atmos.ambient.groundColor.clone()
      .multiplyScalar(atmos.ambient.intensity)
    terrain.setSun(atmos.sample.direction, sunCol, skyCol, groundCol)
  }
  atmos.setTimeOfDay(timeOfDay === 'noon' ? 12.0
    : timeOfDay === 'dusk' ? 18.2 : 7.4)
  atmos.updateEnvironment(true)
  syncTerrainLighting()

  say('route…')
  const route = new RouteSystem(terrain, seed, { roadbook: true, route: STAGE_ROUTE })
  engine.scene.add(route.group)
  terrain.encloseCourse(route.route.polyline)

  say('assets…')
  const library = new AssetLibrary(engine.renderer)
  await library.init()

  const needed = [
    ...CourseFurniture.required(),
    ...SCATTER_ASSETS, ...LANDMARK_ASSETS, ...BIVOUAC_ASSETS,
  ]
  await library.loadMany(needed, (p) => {
    say(`assets ${(p.fraction * 100) | 0}%`)
    net.send({ type: 'load_progress', pct: (p.fraction * 90) | 0 })
  })

  route.buildFurniture(library)

  say('scattering…')
  const scatter = new WorldScatter(terrain, { corridor: 300, density: 1.7 })
  await scatter.build(library, route.route, seed)
  engine.scene.add(scatter.group)

  say('landmarks…')
  const landmarks = new Landmarks(terrain, { reach: 360, density: 2.6 })
  await landmarks.build(library, route.route, seed)
  engine.scene.add(landmarks.group)

  say('bivouac…')
  const bivouac = new Bivouac(terrain, { reach: 300, density: 2.0 })
  await bivouac.build(library, route.route, seed)
  engine.scene.add(bivouac.group)

  say('fx…')
  const fx = new FxSystem(engine.renderer, atmos, {
    seed, capacity: 6144, quality: 0.3, crosswind: 0.12,
    windSpeed: 6, bloom: 0.14, bloomThreshold: 1.0, dustScale: 0.3,
    groundHeight: (x, z) => terrain.height(x, z),
  })

  say('physics…')
  await initPhysics()

  const leg0 = route.route.legs[0]!
  const START_ROLLOUT = -18
  const startAt = pointAt(route.route, Math.max(0, START_ROLLOUT), new THREE.Vector3())
    .addScaledVector(
      new THREE.Vector3(Math.sin(leg0.bearing), 0, Math.cos(leg0.bearing)),
      Math.min(0, START_ROLLOUT))

  const vehicle = spectator ? null : new Vehicle({
    terrain,
    spawn: new THREE.Vector3(startAt.x, 0, startAt.z),
    heading: leg0.bearing,
    speed: 0,
  })
  const input = spectator ? null : new InputSource()
  input?.attach()

  say('buggy…')
  const buggy = await buildBuggy(true, engine.renderer)
  if (buggy.cockpitEye) { /* cockpit rig unshippable upstream; ignored */ }
  if (vehicle) engine.scene.add(buggy.group)
  else buggy.group.visible = false

  const remotes = new RemoteRacers()
  engine.scene.add(remotes.group)

  const props = new PropPhysics(terrain)
  if (vehicle) {
    props.attachPhysics(vehicle.world, {
      scatter, landmarks, bivouac,
      furniture: route.furniture, route: route.route, library,
    })
    engine.add(props)
  }

  // Publish player → route progress.
  const _pubF = new THREE.Vector3()
  function publishPlayer() {
    if (!vehicle) return
    const s = vehicle.state
    _pubF.set(0, 0, 1).applyQuaternion(s.rotation)
    route.setPlayer(s.position, Math.atan2(_pubF.x, _pubF.z), s.velocity)
  }
  publishPlayer()
  if (vehicle && input) {
    engine.add({ name: 'player-publish', fixedUpdate: publishPlayer })
    engine.add({ name: 'vehicle-input', fixedUpdate: (dt) => stepVehicle(dt) })
    engine.add(vehicle)
  }
  engine.add(route)

  // Vehicle view + recovery + touch merge + stats accumulation.
  const _wp = new THREE.Vector3()
  const _wq = new THREE.Quaternion()
  if (vehicle && input) {
    engine.add({
      name: 'vehicle-view',
      fixedUpdate() {
        buggy.root.position.copy(vehicle.state.position)
        buggy.root.quaternion.copy(vehicle.state.rotation)
        for (let i = 0; i < 4; i++) {
          vehicle.wheelTransform(i, _wp, _wq)
          const wm = buggy.wheels[i]
          if (wm) { wm.position.copy(_wp); wm.quaternion.copy(_wq) }
        }
      },
    })
  }

  // ── driving: keyboard/touch sampling, recovery, optional autopilot ──
  // ?auto=1 starts on the pure-pursuit autopilot (ported from main.ts) until
  // a key is pressed — used by the E2E harness and as a demo mode.
  const AUTOPILOT_DEFAULT = new URLSearchParams(location.search)
    .has('auto')
  let autopilotOn = AUTOPILOT_DEFAULT
  const disableAuto = () => { autopilotOn = false }
  addEventListener('keydown', disableAuto, { once: true })
  const _aim = new THREE.Vector3()
  const _fwd = new THREE.Vector3()
  const ZERO_INPUT: VehicleInput = { throttle: 0, brake: 0, steer: 0,
    handbrake: false }
  let playerHint = -1
  let stalledFor = 0
  let rolledFor = 0

  function recover(speed: number) {
    if (!vehicle) return
    const s = vehicle.state
    const sOut = Math.min(
      route.nearest(s.position.x, s.position.z, playerHint).s + 70,
      route.route.length - 40)
    pointAt(route.route, sOut, _aim)
    const rx = _aim.x, rz = _aim.z
    pointAt(route.route, Math.min(sOut + 30, route.route.length - 2), _aim)
    vehicle.respawn(rx, rz, Math.atan2(_aim.x - rx, _aim.z - rz), speed)
    fx.reset()
    playerHint = -1
  }

  function autopilot(): VehicleInput {
    if (!vehicle || !input) return ZERO_INPUT
    const s = vehicle.state
    const near = route.nearest(s.position.x, s.position.z, playerHint)
    playerHint = near.segment
    const look = 34 + s.speed * 1.5
    pointAt(route.route, Math.min(near.s + look, route.route.length - 2), _aim)
    _fwd.set(0, 0, 1).applyQuaternion(s.rotation)
    const want = Math.atan2(_aim.x - s.position.x, _aim.z - s.position.z)
    let err = want - Math.atan2(_fwd.x, _fwd.z)
    while (err > Math.PI) err -= Math.PI * 2
    while (err <= -Math.PI) err += Math.PI * 2
    const steer = THREE.MathUtils.clamp(err * 2.1, -1, 1)

    const h0 = terrain.height(s.position.x, s.position.z)
    let worst = 0, best = 0
    for (const L of [10, 24, 48]) {
      const g = Math.atan2(
        terrain.height(s.position.x + _fwd.x * L,
                       s.position.z + _fwd.z * L) - h0, L)
      if (g < worst) worst = g
      if (g > best) best = g
    }
    let vT = 25 - Math.abs(err) * 20
    if (worst < -0.10) vT = Math.min(vT, 16 + worst * 40)
    if (best > 0.09) vT = Math.max(vT, 17 + best * 26)
    vT = THREE.MathUtils.clamp(vT, 6, 28)

    const rolled = Math.abs(s.chassisRoll) > 1.2
      || Math.abs(s.chassisPitch) > 1.2
    rolledFor = rolled ? rolledFor + 1 / 60 : 0
    stalledFor = s.speed < 1.6 ? stalledFor + 1 / 60 : 0

    let throttle = s.speed > vT ? 0 : 1
    if (stalledFor > 1.2) {
      throttle = (Math.floor(stalledFor * 1.6) % 2) === 0 ? 0.45 : 0.15
    }
    if (rolledFor > 2.5 || stalledFor > 9) {
      rolledFor = 0; stalledFor = 0
      recover(15)
    }
    const over = s.speed - vT
    return {
      throttle,
      brake: over > 2 ? Math.min(1, over / 8) : 0,
      steer, handbrake: false,
    }
  }

  function stepVehicle(dt: number) {
    if (!vehicle || !input) return
    if (!racing) { vehicle.input = ZERO_INPUT; return }
    if (autopilotOn) { vehicle.input = autopilot(); return }
    const cmd = input.sample(dt)
    vehicle.input = cmd
    const st = vehicle.state
    const inverted = Math.abs(st.chassisRoll) > 1.2
      || Math.abs(st.chassisPitch) > 1.2
    rolledFor = inverted ? rolledFor + dt : 0
    if ((cmd.reset || rolledFor > 3.5) && !cmd.reset) {
      rolledFor = 0
      recover(12)
    } else if (cmd.reset) {
      recover(6)
    }
  }

  // Hero dust feed. Mutable mirrors of fx's read-only publish types, so the
  // per-step update writes into one allocation (same pattern as main.ts).
  type MutContact = { -readonly [K in keyof FxWheelContact]: FxWheelContact[K] }
  type MutFx = Omit<{ -readonly [K in keyof FxVehicleState]: FxVehicleState[K] },
    'wheels'> & { wheels: MutContact[] }
  const heroFx: MutFx | null = vehicle ? {
    position: vehicle.state.position,
    quaternion: vehicle.state.rotation,
    velocity: vehicle.state.velocity,
    speed: 0,
    wheels: [0, 1, 2, 3].map(() => ({
      point: new THREE.Vector3(), velocity: new THREE.Vector3(),
      grounded: false, slip: 0, load: 0,
      width: WHEEL_WIDTH * 0.5, surface: 'softSand' as const,
    })),
    airborne: false, id: 'hero',
  } : null
  const fxList: FxVehicleState[] = [
    ...(heroFx ? [heroFx as unknown as FxVehicleState] : []),
    ...(remotes.fxEntries as unknown as FxVehicleState[]),
  ]
  engine.add({
    name: 'fx-feed',
    fixedUpdate(dt: number) {
      if (!vehicle || !heroFx) return
      const s = vehicle.state
      heroFx.speed = s.speed
      heroFx.airborne = s.airborne
      for (let i = 0; i < 4; i++) {
        const w = s.wheels[i]!
        const c = heroFx.wheels[i]!
        c.point.copy(w.contactPoint)
        c.grounded = w.contact
        c.slip = w.slipSpeed
        c.load = w.load
        c.surface = w.surface
        c.velocity.copy(s.velocity)
      }
      if (racing) {
        topSpeed = Math.max(topSpeed, s.speed)
        odo += s.speed * dt
        if (s.airborne) airTime += dt
      }
      fx.setVehicles(fxList)
    },
  })

  engine.add(fx)
  engine.add(terrain)
  engine.add(atmos)

  const cameras = installCameras(engine, {
    vehicle: vehicle ?? (SPECTATOR_SUBJECT as any), terrain, atmos, route, fx,
    rig: spectator ? 'free' : 'chase',
  })

  engine.add({
    name: 'sky-follow',
    fixedUpdate: () => atmos.followCamera(engine.camera),
    update: () => atmos.followCamera(engine.camera),
  })

  const sh = atmos.sun.shadow.camera as THREE.OrthographicCamera
  sh.left = -95; sh.right = 95; sh.top = 95; sh.bottom = -95
  sh.near = 1; sh.far = 3000
  sh.updateProjectionMatrix()
  atmos.sun.shadow.mapSize.set(2048, 2048)
  atmos.sun.shadow.bias = -0.0006
  atmos.sun.shadow.normalBias = 0.05

  engine.add({
    name: 'shadow-follow',
    fixedUpdate() {
      let p: THREE.Vector3 | undefined
      if (vehicle) p = vehicle.state.position
      else {
        const child = remotes.group.children[0]
        if (child) p = child.position
      }
      if (!p || !Number.isFinite(p.x)) return
      atmos.sun.position.copy(atmos.sample.direction).multiplyScalar(900).add(p)
      atmos.sun.target.position.copy(p)
      atmos.sun.target.updateMatrixWorld()
    },
  })

  // Roadbook overlay scene (drawn after composite).
  const uiScene = new THREE.Scene()
  const uiCamera = new THREE.PerspectiveCamera(
    engine.camera.fov, engine.camera.aspect, 0.1, 10)
  route.attachRoadbook(engine.camera, engine.scene, {
    heightFraction: 0.34, parent: uiScene,
  })
  engine.renderer.info.autoReset = false
  engine.renderOverride = (scene, camera) => {
    engine.renderer.info.reset()
    fx.prepare(camera as THREE.PerspectiveCamera, scene)
    fx.render(scene, camera)
    if (route.panel) {
      const prev = engine.renderer.autoClear
      engine.renderer.autoClear = false
      engine.renderer.render(uiScene, uiCamera)
      engine.renderer.autoClear = prev
    }
  }

  if (vehicle) {
    sfxAudio = installAudio(engine, { vehicle, terrain, route, rivals: undefined })
    applySfxVolume()
  }

  world = {
    engine, terrain, route, vehicle, input, remotes, fx, cameras,
    heroGroup: vehicle ? buggy.group : null, fxList, uiScene, seed,
  }

  // Upload the downsampled polyline so the server can score progress.
  uploadRoute(route)
  net.send({ type: 'load_progress', pct: 95 })

  // Per-frame remote smoothing + camera follow for spectators.
  engine.add({
    name: 'mp-update',
    fixedUpdate(dt) {
      remotes.fixedUpdate(dt)
      if (spectator && remotes.remotes.size) {
        let lead: THREE.Vector3 | null = null
        let bestS = -1
        for (const rv of remotes.remotes.values()) {
          if (rv.s > bestS) { bestS = rv.s; lead = rv.pos }
        }
        if (lead && Number.isFinite(lead.x)) {
          cameras.setFreeCamera({
            position: [lead.x - 18, lead.y + 12, lead.z - 18],
            target: [lead.x, lead.y + 1, lead.z],
          })
        }
      }
    },
  })

  say('first frame…')
  await engine.renderFrame()
  boot.classList.add('gone')
  engine.start()
  net.send({ type: 'load_progress', pct: 100, done: true })
  showOverlay('hud', true)
  if (spectator) showOverlay('spectatorBar', true)
}

/** Downsample the polyline to ≤1200 points ≥25 m apart and upload it. */
function uploadRoute(route: RouteSystem) {
  const poly = route.route.polyline
  const pts: number[][] = []
  let lastX = NaN, lastZ = NaN
  for (const p of poly) {
    if (pts.length >= 1190) break
    if (Number.isNaN(lastX) ||
        (p.x - lastX) * (p.x - lastX) + (p.z - lastZ) * (p.z - lastZ) >= 625) {
      pts.push([Math.round(p.x * 10) / 10, Math.round(p.z * 10) / 10])
      lastX = p.x; lastZ = p.z
    }
  }
  const last = poly[poly.length - 1]!
  // Only append the true final vertex if the downsampler didn't already land
  // on (or within a metre of) it — a degenerate final segment gets the whole
  // route rejected server-side.
  if (Number.isNaN(lastX) ||
      (last.x - lastX) * (last.x - lastX) +
      (last.z - lastZ) * (last.z - lastZ) >= 1) {
    pts.push([Math.round(last.x * 10) / 10, Math.round(last.z * 10) / 10])
  }
  net.send({
    type: 'route',
    points: pts,
    length: route.route.length,
    waypoints: route.route.waypoints.map((w) => ({
      s: Math.round(w.distance * 10) / 10, code: w.code,
    })),
  })
}

// ── state ingest + pose stream ────────────────────────────────────────────
function onState(m: StateMsg) {
  if (!world) {
    // Spectator bootstrap: build the world from the live snapshot's seed.
    if (spectator && m.seed !== undefined) {
      buildWorld(m.seed, m.time_of_day ?? 'dawn').then(() => {
        if (world) world.remotes.applyState(m.racers, myId)
      }).catch(console.error)
    }
    return
  }
  const known = rosterCache.map((p) =>
    ({ id: p.id, name: p.name, spectator: p.spectator,
       cosmetics: p.cosmetics ?? null }))
  for (const r of m.racers) {
    if (r.bot && !known.some((k) => k.id === r.id)) {
      const n = parseInt(r.id.replace('bot-', ''), 10)
      known.push({ id: r.id, name: `Rival ${Number.isNaN(n) ? r.id : n}`,
                   spectator: false, cosmetics: null })
    }
  }
  world.remotes.ensureAll(known, world.heroGroup)
  world.remotes.applyState(m.racers, myId)
  if (!racing && !raceOver && !spectator) world.remotes.snap()
  updateHud(m)
}

setInterval(() => {
  if (!world || !racing || !world.vehicle || !net.open) return
  const now = performance.now()
  if (now - lastPoseSent < 100) return
  lastPoseSent = now
  const s = world.vehicle.state
  net.send({
    type: 'pose', x: s.position.x, y: s.position.y, z: s.position.z,
    h: headingOf(s.rotation), v: s.speed,
    s: world.route.progress.state.s,
  })
}, 50)

function headingOf(q: THREE.Quaternion): number {
  const f = new THREE.Vector3(0, 0, 1).applyQuaternion(q)
  return Math.atan2(f.x, f.z)
}

// ── mobile touch controls ────────────────────────────────────────────────
const touch = { steer: 0, throttle: 0, brake: 0, active: false }
function bindTouch() {
  const bindHold = (id: string, on: () => void, off: () => void) => {
    const el = $(id)
    el.addEventListener('touchstart', (e) => { e.preventDefault(); on() }, { passive: false })
    el.addEventListener('touchend', (e) => { e.preventDefault(); off() }, { passive: false })
    el.addEventListener('touchcancel', () => off())
  }
  bindHold('steerLeft', () => { touch.active = true; touch.steer = -1 }, () => { touch.steer = 0 })
  bindHold('steerRight', () => { touch.active = true; touch.steer = 1 }, () => { touch.steer = 0 })
  bindHold('throttleBtn', () => { touch.active = true; touch.throttle = 1 }, () => { touch.throttle = 0 })
  bindHold('brakeBtn', () => { touch.active = true; touch.brake = 1 }, () => { touch.brake = 0 })
  if ('ontouchstart' in window) showOverlay('mobileControls', true)
}
bindTouch()

// Merge touch into whatever InputSource produces.
const _origSample = InputSource.prototype.sample
// eslint-disable-next-line @typescript-eslint/no-explicit-any
InputSource.prototype.sample = function (this: any, dt: number): VehicleInput {
  const v = _origSample.call(this, dt)
  if (touch.active) {
    v.throttle = Math.max(v.throttle, touch.throttle)
    v.brake = Math.max(v.brake, touch.brake)
    if (touch.steer !== 0) v.steer = touch.steer
  }
  return v
}

initLobby().catch(console.error)
// The boot screen hands over to the lobby until a match starts building
// (buildWorld re-shows it for stage generation).
boot.classList.add('gone')
// E2E/harness readiness marker: the module graph fully evaluated.
;(window as unknown as { __mpReady?: boolean }).__mpReady = true
// Probe hook for headless harnesses (touch/spectator verification).
;(window as unknown as { __duneProbe?: () => unknown }).__duneProbe = () => ({
  racing,
  raceOver,
  spectator,
  speed: world?.vehicle?.state.speed ?? -1,
  localS: world ? world.route.progress.state.s : -1,
  touchActive: touch.active,
  touchThrottle: touch.throttle,
  census: world ? world.terrain.classCensus() : null,
})
